import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { trpc } from '../../lib/trpc';
import { getUser, isAuthenticated } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Loader2, Plus, Pencil, Trash, UserPlus, Shield } from 'lucide-react';

export function AdminBuildingsPage() {
  const navigate = useNavigate();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAssignAdminDialogOpen, setIsAssignAdminDialogOpen] = useState(false);
  const [selectedBuilding, setSelectedBuilding] = useState<any>(null);

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    address: '',
    organizationId: '',
  });
  const [adminId, setAdminId] = useState('');

  const utils = trpc.useUtils();
  const { data: buildings, isLoading } = trpc.buildings.list.useQuery();
  const { data: organizations } = trpc.organizations.list.useQuery();
  const { data: buildingAdmins, isLoading: isLoadingAdmins } = trpc.admin.listBuildingAdmins.useQuery();
  
  const createMutation = trpc.buildings.create.useMutation({
    onSuccess: () => {
      toast.success('Building created successfully');
      setIsAddDialogOpen(false);
      setFormData({ code: '', name: '', address: '', organizationId: '' });
      utils.buildings.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const updateMutation = trpc.buildings.update.useMutation({
    onSuccess: () => {
      toast.success('Building updated successfully');
      setIsEditDialogOpen(false);
      setSelectedBuilding(null);
      setFormData({ code: '', name: '', address: '', organizationId: '' });
      utils.buildings.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const deleteMutation = trpc.buildings.delete.useMutation({
    onSuccess: () => {
      toast.success('Building deleted successfully');
      utils.buildings.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const assignAdminMutation = trpc.buildings.assignAdmin.useMutation({
    onSuccess: () => {
      toast.success('Admin assigned successfully');
      setIsAssignAdminDialogOpen(false);
      setAdminId('');
      utils.buildings.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const removeAdminMutation = trpc.buildings.removeAdmin.useMutation({
    onSuccess: () => {
      toast.success('Admin removed successfully');
      utils.buildings.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
    const user = getUser();
    if (user?.role !== 'system_admin') {
      toast.error('Access denied. System admin only.');
      navigate('/admin');
    }
  }, [navigate]);

  const handleEditClick = (building: any) => {
    setSelectedBuilding(building);
    setFormData({
      code: building.code,
      name: building.name,
      address: building.address,
      organizationId: building.organization_id || '',
    });
    setIsEditDialogOpen(true);
  };

  const handleAssignAdminClick = (building: any) => {
    setSelectedBuilding(building);
    setAdminId('');
    setIsAssignAdminDialogOpen(true);
  };

  const handleDeleteClick = async (id: string) => {
    if (confirm('Are you sure you want to delete this building?')) {
      deleteMutation.mutate({ id });
    }
  };

  const handleRemoveAdmin = async (buildingId: string, userId: string) => {
    if (confirm('Are you sure you want to remove this admin?')) {
      removeAdminMutation.mutate({ buildingId, userId });
    }
  };

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  const handleUpdateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBuilding) return;
    updateMutation.mutate({
      id: selectedBuilding.id,
      name: formData.name,
      address: formData.address,
    });
  };

  const handleAssignAdminSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBuilding) return;
    assignAdminMutation.mutate({
      buildingId: selectedBuilding.id,
      userId: adminId,
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between mb-6">
        <div>
          <p className="text-sm text-muted-foreground">Administration</p>
          <h1 className="text-2xl font-semibold text-foreground mt-1">Buildings</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage buildings and their administrators.
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Building
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Building</DialogTitle>
                <DialogDescription>
                  Create a new building. The code must be 2-3 uppercase letters.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Code</Label>
                  <Input
                    id="code"
                    placeholder="e.g. AZ"
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({ ...formData, code: e.target.value.toUpperCase() })
                    }
                    maxLength={3}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    placeholder="Building Name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    placeholder="Address"
                    value={formData.address}
                    onChange={(e) =>
                      setFormData({ ...formData, address: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="organization">Organization</Label>
                  <Select
                    value={formData.organizationId}
                    onValueChange={(value) =>
                      setFormData({ ...formData, organizationId: value })
                    }
                  >
                    <SelectTrigger id="organization">
                      <SelectValue placeholder="Select organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {organizations?.map((org: any) => (
                        <SelectItem key={org.id} value={org.id}>
                          {org.name} ({org.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={createMutation.isPending}>
                    {createMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Create Building
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-b border-border">
              <TableHead className="w-[100px]">Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Assigned Admins</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {buildings?.map((building: any) => (
              <TableRow key={building.id} className="hover:bg-muted/50 transition-colors border-b border-border last:border-0">
                <TableCell className="font-mono font-medium text-primary">{building.code}</TableCell>
                <TableCell className="font-medium text-foreground">{building.name}</TableCell>
                <TableCell className="text-muted-foreground">{building.address}</TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    {building.admin_buildings?.map((ab: any) => (
                      <div
                        key={ab.users.id}
                        className="flex items-center gap-2 text-sm bg-muted/50 px-2 py-1 rounded w-fit"
                      >
                        <Shield className="h-3 w-3 text-primary" />
                        <span className="text-muted-foreground">{ab.users.name || ab.users.username}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4 text-muted-foreground hover:text-destructive hover:bg-transparent"
                          onClick={() => handleRemoveAdmin(building.id, ab.users.id)}
                        >
                          <Trash className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                    {(!building.admin_buildings ||
                      building.admin_buildings.length === 0) && (
                      <span className="text-muted-foreground text-xs italic">
                        No admins assigned
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-primary"
                      onClick={() => handleAssignAdminClick(building)}
                      title="Assign Admin"
                    >
                      <UserPlus className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      onClick={() => handleEditClick(building)}
                      title="Edit Building"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDeleteClick(building.id)}
                      title="Delete Building"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {buildings?.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center h-32 text-muted-foreground">
                  No buildings found. Create a building to get started.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Building</DialogTitle>
            <DialogDescription>
              Update building details. Code cannot be changed.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-code">Code</Label>
              <Input
                id="edit-code"
                value={formData.code}
                disabled
                className="bg-muted"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-address">Address</Label>
              <Input
                id="edit-address"
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                required
              />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isAssignAdminDialogOpen}
        onOpenChange={setIsAssignAdminDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Admin</DialogTitle>
            <DialogDescription>
              Assign a building admin to {selectedBuilding?.name}.
              User must have 'building_admin' role.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAssignAdminSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="admin-select">Building Admin</Label>
              <Select value={adminId} onValueChange={setAdminId} required>
                <SelectTrigger id="admin-select">
                  <SelectValue placeholder="Select a building admin..." />
                </SelectTrigger>
                <SelectContent>
                  {isLoadingAdmins ? (
                    <SelectItem value="loading" disabled>
                      Loading...
                    </SelectItem>
                  ) : buildingAdmins && buildingAdmins.length > 0 ? (
                    buildingAdmins.map((admin: any) => (
                      <SelectItem key={admin.id} value={admin.id}>
                        {admin.name} ({admin.username})
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-admins" disabled>
                      No building admins found
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={assignAdminMutation.isPending}>
                {assignAdminMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Assign Admin
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
